import { useEffect, useRef } from "react"

import {
  removeElementHeight,
  setElementHeight
} from "shared/lib/css/element-height-store"

function getResizeObserverEntryHeight(entry: ResizeObserverEntry) {
  return Math.floor(
    entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
  )
}

export interface UseElementHeightAsCSSVariableOptions {
  /**
   * The name of the CSS variable to set (without the -- prefix)
   * @example "header-height"
   */
  variableName: string
  /**
   * Whether the hook is enabled
   * @default true
   */
  enabled?: boolean
}

/**
 * Measures the height of an element using ResizeObserver and sets it as a CSS variable on the body element.
 * The CSS variable is automatically cleaned up when the hook unmounts.
 *
 * @example
 * ```tsx
 * const headerRef = useRef<HTMLDivElement>(null)
 * useElementHeight(headerRef, { variableName: "header-height" })
 * // Now you can use var(--header-height) in your CSS
 * ```
 */

export function useElementHeight<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  { variableName, enabled = true }: UseElementHeightAsCSSVariableOptions
) {
  const observerRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    if (!enabled || !ref.current) {
      return undefined
    }

    const element = ref.current
    const cssVariableName = variableName.startsWith("--")
      ? variableName
      : `--${variableName}`

    // Set initial height
    const initialHeight = element.getBoundingClientRect().height
    document.body.style.setProperty(cssVariableName, `${initialHeight}px`)
    setElementHeight(variableName, initialHeight)

    // Create ResizeObserver to track height changes
    observerRef.current = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const height = getResizeObserverEntryHeight(entry)
        document.body.style.setProperty(cssVariableName, `${height}px`)
        setElementHeight(variableName, height)
      })
    })

    observerRef.current.observe(element)

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      document.body.style.removeProperty(cssVariableName)
      removeElementHeight(variableName)
    }
  }, [ref, variableName, enabled])
}
